// React imports
import React, { useEffect, useState } from 'react';

// Component imports
import OtherEventsDataGrid from '../../component/DataGrid/OtherEventsDataGrid';
import UserActionsDataGrid from '../../component/DataGrid/UserActionsDataGrid';

// Context imports
import { useRuleContext } from '../../context/RuleStore';
import { useUserContext } from '../../context/UserStore';

// Material-UI imports
import { Card, CardContent, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styles
import './HistoryView.css';

// Styled components for animations
const FastFloatingElement = styled('div')(({ theme }) => ({
  animation: 'fastFloat 3s ease-in-out infinite',
  
  '&:nth-of-type(2n)': {
    animationDelay: '0.5s',
  },
  '&:nth-of-type(3n)': {
    animationDelay: '1s',
  },
  '&:nth-of-type(4n)': {
    animationDelay: '1.5s',
  },
  
  '@keyframes fastFloat': {
    '0%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-20px)',
    },
    '100%': {
      transform: 'translateY(0px)',
    }
  }
}));

// History section styled card with gradient animation
const GradientHistoryCard = styled(Card)(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  flexWrap: 'wrap',
  width: '60%', // Decreased width
  minHeight: '300px', // Increased height
  // Dark purple to blue gradient
  background: 'linear-gradient(to bottom, #2d1b69 0%, #11998e 25%, #38ef7d 50%, #11998e 75%, #2d1b69 100%)',
  backgroundSize: '100% 400%',
  animation: 'purpleBlueVertical 6s ease infinite',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  
  '& .MuiCardContent-root': {
    background: 'rgba(0, 0, 0, 0.8)',
    borderRadius: '8px',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    width: '100%',
  },
  
  // Make text white for better contrast
  '& .MuiTypography-root': {
    color: 'white',
  },
  
  '@keyframes purpleBlueVertical': {
    '0%': {
      backgroundPosition: '0% 0%',
    },
    '50%': {
      backgroundPosition: '0% 100%',
    },
    '100%': {
      backgroundPosition: '0% 0%',
    }
  }
}));

// Data grid container to ensure full width
const DataGridContainer = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100vw',
  margin: '0',
  padding: '0 20px',
  overflowX: 'auto',
  boxSizing: 'border-box',
  
  '& .MuiDataGrid-root': {
    width: '100%',
    minWidth: '900px', // Ensure minimum width for content visibility
    height: 'auto',
    minHeight: '400px',
  },
  
  // Ensure tables and components inside use full width
  '& .MuiTableContainer-root': {
    width: '100%',
    overflowX: 'auto',
    minWidth: '900px',
  },
  
  '& .MuiTable-root': {
    minWidth: '100%',
    width: '100%',
  },
  
  // Handle rainbow card containers
  '& > div': {
    width: '100%',
    boxSizing: 'border-box',
  }
}));

/**
 * HistoryView Component
 * Displays user action history and other events in data grids
 */
export const HistoryView = () => {
  // Context hooks for state management
  const { state: userState, action: userActions } = useUserContext();
  const { state: ruleState, action: ruleActions } = useRuleContext();
  
  // Effect hooks for data initialization
  
  // Initialize user data on component mount
  useEffect(() => {
    userActions.getUserDataAsyncAction();
  }, []);

  // Initialize rules data on component mount
  useEffect(() => {
    ruleActions.getRulesAsyncAction();
  }, []);
  
  return (
    <div style={{ width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      {/* Page Header */}
      <FastFloatingElement style={{ animationDelay: '0.3s', textAlign: 'center', marginBottom: '20px' }}>
        <Typography variant="h3" component="h1" sx={{ 
          color: 'white', 
          fontWeight: 'bold',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
          marginBottom: '10px'
        }}>
          History
        </Typography>
        <Typography variant="h6" component="p" sx={{ 
          color: 'rgba(255,255,255,0.8)', 
          textShadow: '1px 1px 2px rgba(0,0,0,0.6)' 
        }}>
          View your trust points activity and event history
        </Typography>
      </FastFloatingElement>
      
      {/* History Summary Section */}
      <FastFloatingElement style={{ animationDelay: '0.6s' }}>
        <GradientHistoryCard sx={{ margin: 2 }}>
          <CardContent>
            <Typography variant="h4" component="div" sx={{ 
              fontWeight: 'bold', 
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              marginBottom: '20px'
            }}>
              Activity Summary
            </Typography>
            <Typography sx={{ fontSize: 18, marginBottom: '20px', lineHeight: 1.6 }} color="white">
              Track your progress, rule executions, and system events over time. This comprehensive view shows all your trust points activities, including completed rules, earned points, and system interactions.
            </Typography>
            <Typography sx={{ fontSize: 16, fontStyle: 'italic', opacity: 0.9 }} color="white">
              Use the data grids below to explore detailed information about your trust points journey.
            </Typography>
          </CardContent>
        </GradientHistoryCard>
      </FastFloatingElement>
      
      {/* Data Grids Section */}
      <FastFloatingElement style={{ animationDelay: '0.9s' }}>
        <DataGridContainer>
          <UserActionsDataGrid />
        </DataGridContainer>
      </FastFloatingElement>
      
      <FastFloatingElement style={{ animationDelay: '1.2s' }}>
        <DataGridContainer>
          <OtherEventsDataGrid />
        </DataGridContainer>
      </FastFloatingElement>
    </div>
  );
};
